import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { 
  Monitor, 
  Smartphone, 
  ArrowRight,
  Zap,
  Globe,
  Database,
  Wifi
} from 'lucide-react'

export const PlatformIntegration: React.FC = () => {
  const { t } = useTranslation('workforce')
  
  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <div className="container">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            {t('platformIntegration.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('platformIntegration.subtitle')}
          </motion.p>
        </div>

        {/* Integration Diagram */}
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            
            {/* Web Dashboard */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 hover:shadow-2xl transition-shadow duration-500">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Monitor className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('platformIntegration.webDashboard.title')}</h3>
                  <p className="text-gray-600">{t('platformIntegration.webDashboard.description')}</p>
                </div>

                {/* Mock Dashboard */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-blue-100 rounded p-2 text-center">
                      <div className="text-lg font-bold text-blue-600">24</div>
                      <div className="text-xs text-gray-600">{t('platformIntegration.webDashboard.employees')}</div>
                    </div>
                    <div className="bg-green-100 rounded p-2 text-center">
                      <div className="text-lg font-bold text-green-600">156</div>
                      <div className="text-xs text-gray-600">{t('platformIntegration.webDashboard.tasks')}</div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="bg-white rounded px-2 py-1 text-xs flex justify-between">
                      <span>{t('platformIntegration.webDashboard.planning')}</span>
                      <span className="text-blue-600">●</span>
                    </div>
                    <div className="bg-white rounded px-2 py-1 text-xs flex justify-between">
                      <span>{t('platformIntegration.webDashboard.analytics')}</span>
                      <span className="text-green-600">●</span>
                    </div>
                  </div>
                </div>

                <ul className="text-sm space-y-2">
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span>{t('platformIntegration.webDashboard.features.0')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span>{t('platformIntegration.webDashboard.features.1')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span>{t('platformIntegration.webDashboard.features.2')}</span>
                  </li>
                </ul>
              </div>

              {/* Connection Arrow */}
              <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                <div className="flex items-center">
                  <ArrowRight className="w-6 h-6 text-orange-500" />
                  <div className="w-8 h-0.5 bg-orange-500"></div>
                </div>
              </div>
            </motion.div>

            {/* Central Integration Hub */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="text-center">
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-red-600 rounded-full opacity-20 animate-pulse"></div>
                  <div className="relative w-24 h-24 bg-gradient-to-r from-orange-600 to-red-600 rounded-full flex items-center justify-center mx-auto">
                    <Zap className="w-10 h-10 text-white" />
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('platformIntegration.centralHub.title')}</h3>
                <p className="text-gray-600 mb-6">
                  {t('platformIntegration.centralHub.description')}
                </p>

                {/* Integration Features */}
                <div className="space-y-3">
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <Wifi className="w-4 h-4 text-green-500" />
                    <span className="text-gray-700">{t('platformIntegration.centralHub.features.0')}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <Database className="w-4 h-4 text-blue-500" />
                    <span className="text-gray-700">{t('platformIntegration.centralHub.features.1')}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <Globe className="w-4 h-4 text-purple-500" />
                    <span className="text-gray-700">{t('platformIntegration.centralHub.features.2')}</span>
                  </div>
                </div>
              </div>

              {/* Mobile Connection Arrow */}
              <div className="hidden lg:block absolute top-1/2 -left-4 transform -translate-y-1/2">
                <div className="flex items-center">
                  <div className="w-8 h-0.5 bg-orange-500"></div>
                  <ArrowRight className="w-6 h-6 text-orange-500 transform rotate-180" />
                </div>
              </div>
            </motion.div>

            {/* Mobile App */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 hover:shadow-2xl transition-shadow duration-500">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Smartphone className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('platformIntegration.mobileApp.title')}</h3>
                  <p className="text-gray-600">{t('platformIntegration.mobileApp.description')}</p>
                </div>

                {/* Mock Mobile Interface */}
                <div className="bg-gray-900 rounded-2xl p-3 mx-auto w-40 mb-6">
                  <div className="bg-white rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs font-bold">WorkForce</div>
                      <div className="text-xs text-gray-500">09:23</div>
                    </div>
                    <div className="text-center mb-3">
                      <div className="text-lg font-bold text-orange-600">02:47:15</div>
                      <div className="text-xs text-gray-600">{t('platformIntegration.mobileApp.working')}</div>
                    </div>
                    <div className="space-y-2">
                      <div className="bg-orange-100 rounded px-2 py-1 text-xs text-center">
                        <div className="flex items-center justify-center gap-1">
                          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                          {t('platformIntegration.mobileApp.clockIn')}: 06:30
                        </div>
                      </div>
                      <div className="bg-blue-100 rounded px-2 py-1 text-xs text-center">
                        5 {t('platformIntegration.mobileApp.pendingTasks')}
                      </div>
                      <div className="bg-green-100 rounded px-2 py-1 text-xs text-center">
                        💬 3 {t('platformIntegration.mobileApp.messages')}
                      </div>
                    </div>
                  </div>
                </div>

                <ul className="text-sm space-y-2">
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <span>{t('platformIntegration.mobileApp.features.0')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <span>{t('platformIntegration.mobileApp.features.1')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <span>{t('platformIntegration.mobileApp.features.2')}</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Tech Architecture Preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center p-8 bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl border border-orange-200"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            {t('platformIntegration.architecture.title')}
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            {t('platformIntegration.architecture.description')}
          </p>
          
          <div className="flex flex-wrap gap-3 justify-center">
            {['React 19', 'Spring Boot 3', 'PostgreSQL', 'WebSocket', 'PWA', 'JWT + OAuth2', 'Docker'].map((tech) => (
              <span
                key={tech}
                className="px-4 py-2 bg-white text-gray-700 rounded-full text-sm font-medium shadow-sm border border-gray-200"
              >
                {tech}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}